import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, Square, Play, Pause, Trash2, Send } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';

export interface VoiceRecorderProps {
  /** Called with the recorded blob + duration when the user taps "Send" */
  onSend: (blob: Blob, durationSec: number) => void | Promise<void>;
  /** Called when the user discards the recording */
  onDiscard?: () => void;
  /** Max recording duration in seconds (default 45) */
  maxDurationSec?: number;
  /** Whether the send button should show a loading spinner */
  sending?: boolean;
}

type RecorderState = 'idle' | 'requesting' | 'recording' | 'preview';

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onSend,
  onDiscard,
  maxDurationSec = 45,
  sending = false,
}) => {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobDuration, setBlobDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playProgress, setPlayProgress] = useState(0);
  const [noMedia, setNoMedia] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopTimers();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
  };

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setNoMedia(true);
      return;
    }
    if (!mountedRef.current) return;
    setState('requesting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const e = err as { name?: string };
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        toast.error('Microphone permission denied. Please allow access in your browser settings.');
      } else {
        toast.error('Could not access microphone.');
      }
      setState('idle');
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : '';

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      if (!mountedRef.current) return;
      const recorded = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
      setBlob(recorded);
      setState('preview');
    };

    recorder.start(100); // collect every 100ms
    if (!mountedRef.current) { recorder.stop(); return; }
    setState('recording');
    setElapsed(0);

    timerRef.current = setInterval(() => {
      if (!mountedRef.current) return;
      setElapsed((prev) => {
        const next = prev + 1;
        // Warn at maxDurationSec - 5 seconds
        if (next === maxDurationSec - 5) {
          toast('5 seconds remaining', { icon: '⏰' });
        }
        return next;
      });
    }, 1000);

    // Auto-stop at max
    maxTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      stopRecording();
    }, maxDurationSec * 1000);
  }, [maxDurationSec]);

  const stopRecording = useCallback(() => {
    stopTimers();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      setBlobDuration(elapsed);
      mediaRecorderRef.current.stop();
    }
  }, [elapsed]);

  const handleDiscard = () => {
    setBlob(null);
    setElapsed(0);
    setPlaying(false);
    setPlayProgress(0);
    setBlobDuration(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setState('idle');
    onDiscard?.();
  };

  const handlePlayPause = () => {
    if (!blob) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(URL.createObjectURL(blob));
      audioRef.current.ontimeupdate = () => {
        if (!audioRef.current) return;
        const pct = audioRef.current.duration
          ? audioRef.current.currentTime / audioRef.current.duration
          : 0;
        setPlayProgress(pct);
      };
      audioRef.current.onended = () => {
        setPlaying(false);
        setPlayProgress(0);
      };
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => toast.error('Could not play audio'));
      setPlaying(true);
    }
  };

  const handleSend = async () => {
    if (!blob) return;
    await onSend(blob, blobDuration || elapsed);
  };

  // ── Render: "no secure context" fallback ──────────────────────────────────────
  if (noMedia) {
    return (
      <div
        className="group relative"
        title="Voice notes require a secure context (https or localhost)"
      >
        <button
          disabled
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-surface-alt text-text-muted opacity-50 cursor-not-allowed"
        >
          <Mic size={16} />
        </button>
        <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-surface-elevated px-2 py-1 text-[10px] text-text-secondary shadow-elevated opacity-0 group-hover:opacity-100 transition">
          Voice needs https or localhost
        </span>
      </div>
    );
  }

  // ── Render: idle (microphone trigger button) ──────────────────────────────────
  if (state === 'idle' || state === 'requesting') {
    return (
      <button
        onClick={startRecording}
        disabled={state === 'requesting'}
        title="Record voice note"
        className={clsx(
          'flex h-9 w-9 items-center justify-center rounded-xl border transition',
          'border-border bg-surface-alt text-text-secondary',
          'hover:border-primary hover:bg-primary-soft hover:text-primary',
          state === 'requesting' && 'opacity-60 cursor-wait',
        )}
      >
        <Mic size={16} />
      </button>
    );
  }

  // ── Render: recording ─────────────────────────────────────────────────────────
  if (state === 'recording') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-danger/40 bg-danger-soft px-3 py-2">
        {/* Pulse animation */}
        <span className="relative flex h-3 w-3 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-danger" />
        </span>
        {/* Waveform bars */}
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <span
              key={i}
              className="w-0.5 rounded-full bg-danger animate-[waveBar_0.8s_ease-in-out_infinite_alternate]"
              style={{
                height: `${8 + (i % 3) * 5}px`,
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>
        <span className="min-w-[2.5rem] font-mono text-sm font-semibold text-danger">
          {formatDuration(elapsed)}
        </span>
        <button
          onClick={stopRecording}
          title="Stop recording"
          className="ml-1 flex h-7 w-7 items-center justify-center rounded-lg bg-danger text-white hover:bg-danger/80 transition"
        >
          <Square size={12} fill="currentColor" />
        </button>
      </div>
    );
  }

  // ── Render: preview ───────────────────────────────────────────────────────────
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 w-full">
      {/* Play / Pause */}
      <button
        onClick={handlePlayPause}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-white hover:bg-primary/80 transition"
      >
        {playing ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
      </button>

      {/* Progress bar */}
      <div className="relative flex-1 h-1.5 rounded-full bg-border overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-primary transition-all"
          style={{ width: `${playProgress * 100}%` }}
        />
        {/* Waveform placeholder bars (purely decorative) */}
        <div className="absolute inset-0 flex items-center justify-around pointer-events-none opacity-20">
          {Array.from({ length: 20 }).map((_, i) => (
            <span key={i} className="w-px rounded-full bg-primary" style={{ height: `${(i % 5) * 20 + 20}%` }} />
          ))}
        </div>
      </div>

      {/* Duration */}
      <span className="shrink-0 font-mono text-xs text-text-muted">
        {formatDuration(blobDuration || elapsed)}
      </span>

      {/* Discard */}
      <button
        onClick={handleDiscard}
        title="Discard"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border text-text-muted hover:border-danger hover:text-danger transition"
      >
        <Trash2 size={13} />
      </button>

      {/* Send */}
      <Button
        size="sm"
        loading={sending}
        onClick={handleSend}
        iconLeft={<Send size={13} />}
      >
        Send
      </Button>
    </div>
  );
};
